/* RiftX storefront motion: header state, reveals, counters, hero tilt, spotlight parallax,
   AJAX add-to-cart into the theme's cart drawer, smooth FAQ. Moving strips never pause on hover. */
(() => {
  if (window.__rxHome) return;
  window.__rxHome = true;
  const d = document;
  const root = d.documentElement;
  root.classList.add('rx-js');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(pointer: fine)').matches;

  const ready = (fn) => (d.readyState === 'loading' ? d.addEventListener('DOMContentLoaded', fn) : fn());

  ready(() => {
    /* header: solid bar, deeper shadow once the page scrolls */
    const onScroll = () => d.body.classList.toggle('rx-scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* numbers are printed as they are; counting them up read as fake (owner, 31 Oct) */
    const count = () => {};

    /* reveal: observe whole sections (Chrome ignores clipped children) */
    const targets = d.querySelectorAll('[data-rx-reveal]');
    if ('IntersectionObserver' in window && !reduce) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          count(e.target);
          io.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
      targets.forEach((t) => io.observe(t));
    } else {
      targets.forEach((t) => { t.classList.add('is-in'); count(t); });
    }
    /* safety net: anything already on screen is shown even if the observer never fires (in-app browsers) */
    let net = 0;
    const sweep = () => {
      net = 0;
      const vh = window.innerHeight;
      targets.forEach((t) => {
        if (t.classList.contains('is-in')) return;
        const r = t.getBoundingClientRect();
        if (r.top < vh * 0.98 && r.bottom > 0) { t.classList.add('is-in'); count(t); }
      });
    };
    window.addEventListener('scroll', () => { if (!net) net = setTimeout(sweep, 450); }, { passive: true });
    setTimeout(sweep, 1200);

    /* hero: entrance, keep the video playing only while visible, pointer tilt */
    const hero = d.querySelector('.rx-hero');
    if (hero) {
      requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-ready')));
      setTimeout(() => hero.classList.add('is-settled'), 2200);
      const vid = hero.querySelector('video');
      if (vid) {
        vid.muted = true;
        vid.setAttribute('muted', '');
        if (reduce) { vid.removeAttribute('autoplay'); vid.pause(); }
        else if ('IntersectionObserver' in window) {
          new IntersectionObserver(([e]) => { if (e.isIntersecting) vid.play().catch(() => {}); else vid.pause(); }, { threshold: 0.05 }).observe(hero);
        }
      }
      const stack = hero.querySelector('[data-rx-tilt]');
      if (stack && fine && !reduce) {
        const cards = [...stack.querySelectorAll('.rx-hcard')];
        let mx = 0; let my = 0; let raf = 0;
        const apply = () => {
          raf = 0;
          cards.forEach((c, i) => {
            const depth = 10 + i * 7;
            c.style.setProperty('--tx', `${(mx * depth).toFixed(1)}px`);
            c.style.setProperty('--ty', `${(my * depth).toFixed(1)}px`);
          });
        };
        hero.addEventListener('pointermove', (e) => {
          const r = hero.getBoundingClientRect();
          mx = (e.clientX - r.left) / r.width - 0.5;
          my = (e.clientY - r.top) / r.height - 0.5;
          if (!raf) raf = requestAnimationFrame(apply);
        });
        hero.addEventListener('pointerleave', () => { mx = 0; my = 0; if (!raf) raf = requestAnimationFrame(apply); });
      }
    }

    /* banner row on phones: dots follow the card in view (no auto-advance) */
    d.querySelectorAll('[data-rx-banners]').forEach((sec) => {
      const track = sec.querySelector('[data-rx-banners-track]');
      const dots = [...sec.querySelectorAll('.rx-banners__dot')];
      if (!track || dots.length < 2 || !('IntersectionObserver' in window)) return;
      const cards = [...track.children];
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || e.intersectionRatio < 0.6) return;
          const i = cards.indexOf(e.target);
          dots.forEach((dot, j) => dot.classList.toggle('is-on', j === i));
        });
      }, { root: track, threshold: [0.6] });
      cards.forEach((c) => io.observe(c));
    });

    /* spotlight: product plate drifts against the scroll */
    const plates = [...d.querySelectorAll('[data-rx-parallax]')];
    if (plates.length && !reduce) {
      let ticking = false;
      const upd = () => {
        ticking = false;
        const vh = window.innerHeight;
        plates.forEach((p) => {
          const r = p.getBoundingClientRect();
          if (r.bottom < -200 || r.top > vh + 200) return;
          const c = (r.top + r.height / 2 - vh / 2) / vh;
          p.style.setProperty('--py', `${(c * -36).toFixed(1)}px`);
        });
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(upd); } }, { passive: true });
      upd();
    }

    /* add to cart from custom sections: same request the theme's product form makes */
    d.addEventListener('submit', async (ev) => {
      const form = ev.target.closest('form[data-rx-atc]');
      if (!form) return;
      ev.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const label = btn ? btn.innerHTML : '';
      if (btn) btn.classList.add('is-loading');
      const cart = d.querySelector('cart-drawer') || d.querySelector('cart-notification');
      try {
        const fd = new FormData(form);
        if (cart && typeof cart.getSectionsToRender === 'function') {
          fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
          fd.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(btn);
        }
        const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const res = await fetch(url, { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' } });
        const data = await res.json();
        if (data.status) throw new Error(data.description || 'add failed');
        if (cart && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
        if (btn) { btn.classList.add('is-added'); btn.innerHTML = btn.dataset.added || 'Added'; setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = label; }, 2200); }
      } catch (e) {
        form.removeAttribute('data-rx-atc');
        form.submit();
      } finally {
        if (btn) btn.classList.remove('is-loading');
      }
    });

    /* collection intro: three lines on phones, full text one tap away (all of it stays in the HTML) */
    const desc = d.querySelector('.collection-hero__description');
    if (desc && window.matchMedia('(max-width: 749px)').matches && desc.scrollHeight > 130) {
      desc.classList.add('rx-clamp');
      const more = d.createElement('button');
      more.type = 'button';
      more.className = 'rx-more';
      more.textContent = 'Read more';
      more.setAttribute('aria-expanded', 'false');
      more.addEventListener('click', () => {
        const open = desc.classList.toggle('is-open');
        more.textContent = open ? 'Show less' : 'Read more';
        more.setAttribute('aria-expanded', String(open));
      });
      desc.after(more);
    }

    /* FAQ: animated open and close */
    d.querySelectorAll('.rx-faq__list details').forEach((det) => {
      const sum = det.querySelector('summary');
      const body = det.querySelector('.rx-faq__a');
      if (!sum || !body || reduce) return;
      sum.addEventListener('click', (e) => {
        e.preventDefault();
        if (det.dataset.busy) return;
        det.dataset.busy = '1';
        if (det.open) {
          const h = body.scrollHeight;
          const a = body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 340, easing: 'cubic-bezier(.2,.7,.1,1)' });
          a.onfinish = () => { det.open = false; delete det.dataset.busy; };
        } else {
          det.open = true;
          const h = body.scrollHeight;
          const a = body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' });
          a.onfinish = () => { delete det.dataset.busy; };
        }
      });
    });
  });
})();
